import { useUtils as useI18nUtils } from '@core/libs/i18n';
import toastHelper from '@core/helpers/toast.helper.js';
import useService from '@/auth/service/useService';
import store from '@/store';
import { isUserLoggedIn } from '@/auth/utils';
import valuableCurrencies from '@/constants/currencies.json';
import { ROLE_ENUM } from '@/constants/roles';

const { t } = useI18nUtils();
let exportInterval = null;
let importInterval = null;

export const helper = {
  methods: {
    getRoleName(role) {
      let name = role;
      const normalized = String(role || '')
        .replace(/^ROLE_/, '')
        .trim();

      switch (normalized) {
        case ROLE_ENUM.CUSTOMER_USER:
          name = t('Customer User');
          break;
        case ROLE_ENUM.ADMIN:
          name = t('Admin');
          break;
        case ROLE_ENUM.SUPERADMIN:
          name = t('Super Admin');
          break;
        case ROLE_ENUM.ACCOUNT_ADMIN:
          name = t('Account Admin');
          break;
        case ROLE_ENUM.CUSTOMER_MANAGER:
          name = t('Customer Manager');
          break;
        case ROLE_ENUM.OPERATION_MANAGER:
          name = t('Operation Manager');
          break;
        case ROLE_ENUM.SYSTEM:
          name = t('System');
          break;
        default:
          name = t(role) || role;
          break;
      }

      return name;
    },

    getDocumentTypeName(type) {
      let text = 'light-';

      switch (type) {
        case 1:
          text = t('Invoice');
          break;
        case 2:
          text = t('Package List');
          break;
        case 3:
          text = t('Customs Form');
          break;
        case 4:
          text = t('MSDS Document');
          break;
        case 5:
          text = t('FDA Document');
          break;
        case 6:
          text = t('TSCA Document');
          break;
        case 7:
          text = t('Other');
          break;
        case 8:
          text = t('Proforma');
          break;
        case 9:
          text = t('Waybill');
          break;
        case 10:
          text = t('Customs Declaration CN22');
          break;
        case 11:
          text = t('Customs Declaration CN23');
          break;
        default:
          text = t('Default');
      }

      return text;
    },
    getPackageTypeName(type) {
      let text = type;

      switch (Number(type)) {
        case 1:
          text = t('Box');
          break;
        case 2:
          text = t('Envelope');
          break;
        case 3:
          text = t('Pallet');
          break;
        case 4:
          text = t('Tube');
          break;
        case 5:
          text = t('Other');
          break;
        case 6:
          text = t('Contract');
          break;
        default:
          break;
      }

      return text;
    },

    getPostTypeName(type) {
      let text = type;

      switch (Number(type)) {
        case 1:
          text = t('Sample');
          break;
        case 2:
          text = t('Micro');
          break;
        case 3:
          text = t('Gift');
          break;
        case 4:
          text = t('Sale');
          break;
        case 5:
          text = t('Personal');
          break;
        case 6:
          text = t('Other');
          break;
        case 7:
          text = t('Document');
          break;
        default:
          break;
      }

      return text;
    },
    getCustomerSelectBox(customers) {
      const data = customers.map(value => {
        const singleCustomer = {};
        singleCustomer.text = value.name;
        singleCustomer.value = value.id;

        return singleCustomer;
      });
      return data;
    },
    setLogout(message = true) {
      if (isUserLoggedIn()) {
        store.dispatch('auth/logout');
      }

      if (message) {
        this.showSuccessMessage(this.$t('Logout Success Message'), this.$t('Logout Successful'));
      }
    },
    getDataFromToken(token) {
      const jsonData = this.parseJwt(token);

      return this.getUserData(jsonData);
    },
    parseJwt(token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonData = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );

      return JSON.parse(jsonData);
    },

    getUserData(jsonData) {
      const jsonPayload = {
        id: jsonData.id,
        role: jsonData.role,
        username: jsonData.username,
        email: jsonData.email,
        fullName: this.$t('User'),
        companyId: jsonData.companyId || jsonData.company_id
      };

      if (jsonData.name) {
        jsonPayload.fullName = jsonData.name;
        if (jsonData.surname) {
          jsonPayload.fullName += ` ${jsonData.surname}`;
        }
      }

      return jsonPayload;
    },

    validateEmail(email) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
      return emailPattern.test(email);
    },

    getFilteredObject(values) {
      const asArray = Object.entries(values || {});
      const filteredObject = {};

      const filtered = asArray.filter(([_key, value]) => {
        if (value === '' || value === null || typeof value === 'undefined') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
          return false;
        return true;
      });

      if (!filtered.length) {
        return {};
      }

      filtered.forEach(([key, value]) => {
        filteredObject[key] = value;
      });

      return filteredObject;
    },

    // getDocumentTypes() {
    //   return [
    //     { text: t('Invoice'), value: 1 },
    //     { text: t('Package List'), value: 2 },
    //     { text: t('Customs Form'), value: 3 },
    //     { text: t('MSDS Document'), value: 4 },
    //     { text: t('FDA Document'), value: 5 },
    //     { text: t('TSCA Document'), value: 6 },
    //     { text: t('Other'), value: 7 },
    //     { text: t('Proforma'), value: 8 },
    //     { text: t('Waybill'), value: 9 },
    //   ]
    // },

    // getCustomerDocumentTypes() {
    //   return [
    //     {text: "Vergi Levhası",  key: t('Tax Plate'), value: 1 },
    //     {text: "Ticaret sicil kayıt belgesi", key: t('Trade Registry'), value: 2 },
    //     {text: "İmza Sirküleri", key: t('Signature Circular'), value: 3 },
    //     {text: "Faaliyet Belgesi", key: t('Activity Certificate'), value: 4 },
    //     {text: "Islak İmzalı Banka Bilgisi", key: t('Wet Signed Bank Info'), value: 5 },
    //   ]
    // },

    // getDocumentStatusTypes() {
    //   return [
    //     {text: "Döküman onaylandı.",  key: t('APPROVED'), value: 1 },
    //     {text: "Döküman kabul edilmedi.", key: t('REJECTED'), value: 2 },
    //     {text: "Döküman onay için beklemede.", key: t('IN_REVIEW'), value: 0 },
    //   ]
    // },

    getConsignmentProviderPricingType() {
      return useService.getConsignmentProviderPricingType().then(response => response.data);
    },
    permissionsOptionsList() {
      return useService.getPermissions().then(response => response.data);
    },

    getConsignmentStatus() {
      return useService.getConsignmentStatus().then(response => response.data);
    },

    getOfferStatus() {
      return useService.getOfferStatus().then(response => response.data);
    },

    getConsignmentDocumentType() {
      return useService.getConsignmentDocumentType().then(response => response.data);
    },

    getConsignmentPostType() {
      return useService.getConsignmentPostType().then(response => response.data);
    },

    getConsignmentPackageType() {
      return useService.getConsignmentPackageType().then(response => response.data);
    },

    getPackageStatus() {
      return useService.getPackageStatus().then(response => response.data);
    },

    getAddressType() {
      return useService.getAddressType().then(response => response.data);
    },

    getCustomerPaymentType() {
      return useService.getCustomerPaymentType().then(response => response.data);
    },

    getPackages() {
      return useService.getPackages().then(response => response.data);
    },

    getDepartmentRuleKey() {
      return useService.getDepartmentRuleKey().then(response => response.data);
    },

    getDepartmentRuleType() {
      return useService.getDepartmentRuleType().then(response => response.data);
    },

    getDepartmentRuleCondition() {
      return useService.getDepartmentRuleCondition().then(response => response.data);
    },

    getProviderAdditionalFeeStatus() {
      return useService.getProviderAdditionalFeeStatus().then(response => response.data);
    },

    getProviderAdditionalFeePricingType() {
      return useService.getProviderAdditionalFeePricingType().then(response => response.data);
    },

    getProviderRulePricingType() {
      return useService.getProviderRulePricingType().then(response => response.data);
    },

    getCustomerDocumentType() {
      return useService.getCustomerDocumentType().then(response => response.data);
    },

    getUserStatus() {
      return useService.getUserStatus().then(response => response.data);
    },

    getProviders(args) {
      return useService.getAllProviders(args).then(response => response.data);
    },

    getTaxStatus() {
      return useService.getTaxStatus().then(response => response.data);
    },
    getTaxApplicationScope() {
      return useService.constantService.getTaxApplicationScope().then(response => response.data);
    },
    getCustomers({ paginated } = { paginated: true }) {
      return useService
        .getAllCustomers({ paginated })
        .then(response => response.data)
        .catch(() => {
          return [];
        });
    },
    regionMapping(data) {
      const { state_code, ...rest } = data;

      return {
        ...rest,
        country: data.country.value || data.country,
        state: data.state.text || data.state,
        city: data.city.text || data.city,
        postal_code: data.postal_code.value || data.postal_code,
        ...(state_code && { state_code })
      };
    },

    getDistrictRequest(data) {
      const newData = {
        value: data.id,
        text: data.name
      };

      if (data.iso2) newData.iso2 = data.iso2;
      if (data.iso3) newData.iso3 = data.iso3;
      if (data.code) newData.code = data.code;

      return newData;
    },

    getPostalCodeRequest(data) {
      return {
        value: data.id,
        text: data.zipcode,
        combinedLabel: `${data.zipcode} - ${data.place}`
      };
    },

    base64ToBlob(base64, type = 'application/octet-stream') {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type });
    },
    getBase64PdfFile(data) {
      const base64 = data;
      const blob = this.base64ToBlob(base64, 'application/pdf');
      return URL.createObjectURL(blob);
    },

    getBase64ImageFile(data, type) {
      const base64 = data;
      let contentType = '';

      if (type === 'gif') {
        contentType = 'image/gif';
      } else if (type === 'png') {
        contentType = 'image/png';
      } else if (type === 'jpg' || type === 'jpeg') {
        contentType = 'image/jpeg';
      }

      const blob = this.base64ToBlob(base64, contentType);

      return URL.createObjectURL(blob);
    },
    providersCheapestMapping(response) {
      const providers = response;

      providers.sort((a, b) => a.price - b.price);
      const lowestPrice = providers[0].price;

      providers.forEach(obj => {
        if (obj.price === lowestPrice) {
          obj.cheapest = true;
        }
      });

      return providers;
    },
    showErrorMessage(message, title) {
      toastHelper.showErrorMessage(message, title);
    },
    showSuccessMessage(message, title) {
      toastHelper.showSuccessMessage(message, title);
    },
    showWarningMessage(message, title) {
      toastHelper.showWarningMessage(message, title);
    },
    showInfoMessage(message, title) {
      toastHelper.showInfoMessage(message, title);
    },
    removeTabindexFromModal(modalId) {
      setTimeout(() => {
        const modalElement = document.getElementById(modalId);

        if (modalElement) {
          const modalContent = modalElement.querySelector('.modal-content');

          if (modalContent) {
            modalContent.removeAttribute('tabindex');
          }
        }
      }, 250);
    },
    exportDocument(requestData) {
      return useService.documentExportStart(requestData).then(response => response.data.id);
    },
    exportConsignment(requestData) {
      return useService.documentExportStart(requestData);
    },
    documentExportControl(id) {
      exportInterval = setInterval(() => {
        useService.documentExportControl(id).then(controlResponse => {
          if (this.$store.getters['app/exportProgress'] !== controlResponse.data.percent) {
            this.$store.commit('app/SET_EXPORT_PROGRESS', controlResponse.data.percent);
          }
          if (controlResponse.data.percent === 100) {
            useService.documentExportDownload(controlResponse.data.id).then(downloadResponse => {
              this.$store.commit('app/SET_EXPORT_FILE', downloadResponse);
            });
            this.stopExportInterval();
          }
        });
      }, 1000);
    },
    stopExportInterval() {
      clearInterval(exportInterval);
    },

    importDocument(files) {
      return useService.documentImportStart(files);
    },
    importDocumentProgress(id) {
      importInterval = setInterval(() => {
        useService.documentImportProgress(id).then(controlResponse => {
          if (this.$store.getters['app/importProgress'] !== controlResponse.data.totalCount) {
            this.$store.commit('app/SET_IMPORT_PROGRESS', controlResponse.data.totalCount);
          }
          if (controlResponse.data.totalCount === controlResponse.data.totalRowCount) {
            this.stopImportInterval();
            this.showSuccessMessage(this.$t('Import Success Message'), this.$t('Successful'));
            this.$refs['invoice-upload-modal'].hide();
            this.$router.go(0);
          }
        });
      }, 1000);
    },
    stopImportInterval() {
      clearInterval(importInterval);
    },

    // Taslak sevkiyat oluşturma isteği şuan boş obje gönderilmektedir.
    /* getPackagesMapped(packages) {
                                      let packageInformations = []
                                      let singlePackage = {}
                                      if(typeof packages !== 'undefined' && packages && packages.length > 0) {
                                        packages.forEach(item => {
                                          singlePackage = {}
            
                                          if(item.items && item.items.length > 0) {
                                            item.items.forEach(product => {
                                              singlePackage = {}
            
                                              singlePackage = {
                                                description: product.description || "",
                                                gtip: product.gtip || "",
                                                origin: product.origin || "",
                                                currency_code: product.currency || "",
                                                quantity: product.quantity || "",
                                                category: product.category || "",
                                                unit_price: product.unit_price || ""
                                              }
                                              packageInformations.push(singlePackage)
                                            })
                                          }
            
                                        })
                                      }
            
                                      return packageInformations
                                    }, */
    formalizeShipmentConfirm(consignmentId, packages) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Are you sure you want to formalize the shipment?')}`,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          this.makeItOfficial(consignmentId, packages).then(() => {
            this.showSuccessMessage(`${this.$t('Draft shipment successfully formalized')}`);
          });
        }
      });
    },

    createSpecialOffer(consignmentId) {
      this.$router.push({ name: 'create_admin_special_offer', params: { consignmentId } });
    },

    makeItOfficial(consignmentId) {
      return new Promise(resolve => {
        useService.makeItOfficial(consignmentId, {}).then(res => {
          resolve(res);
        });
      });
    },

    applyFilters(filters) {
      this.$store.dispatch('app/setAppliedFilters', { [this.$route.name]: { ...filters } });
    },

    getAppliedFilters() {
      if (this.$store.state.app.appliedFilters) {
        return this.$store.state.app.appliedFilters[this.$route.name];
      }
      return null;
    },
    cancelConsignment(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Make Cancel Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await this.cancelConsignmentRequest(id)
            .then(response => {
              this.showSuccessMessage(`${response.message || this.$t('Consignment was deleted')}`);

              this.filter.page = 1;
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    },
    async cancelConsignmentRequest(id) {
      return useService.cancelConsignment(id);
    },
    editConsignment(id) {
      this.$router.push(`/consignments/edit/${id}`);
    },
    resetFilters(values) {
      Object.keys(values).forEach(key => {
        if (values[key] !== '') {
          values[key] = '';
        }
      });
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY'
      }).format(value);
    },
    formatPrice(price, currency) {
      if (price === undefined || price === null) return '—';
      let sym = currency || 'TRY';
      if (sym === 'TRY') {
        sym = '₺';
      } else if (sym === 'USD') {
        sym = '$';
      } else if (sym === 'EUR') {
        sym = '€';
      }
      return `${sym}${parseFloat(price).toFixed(2)}`;
    },
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '';
      return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(parseFloat(amount));
    },
    formatNumber(value) {
      return new Intl.NumberFormat('en-US', { useGrouping: true }).format(value);
    },
    formatPercentage(value) {
      return new Intl.NumberFormat('us-US', {
        style: 'percent',
        maximumFractionDigits: 1,
        minimumFractionDigits: 1
      }).format(parseFloat(value) / 100);
    },
    getDispatchOptions() {
      return useService.getProviderDispatchMode().then(response => response.data);
    },

    getExportStatusLabel() {
      return useService.getExportStatus().then(response => response.data);
    },

    getExportTypeLabel() {
      return useService.getExportType().then(response => response.data);
    },

    formatDateToCustom(inputDate) {
      if (!inputDate || inputDate === null || inputDate === undefined || inputDate === '') {
        return '-';
      }

      const date = new Date(inputDate);

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${day}-${month}-${year} ${hours}:${minutes}`;
    },

    getCurrencyCode(code) {
      const currency = valuableCurrencies?.find(c => c?.code === code);
      return currency ? currency?.code_text : code;
    },

    formatBytes(bytes, decimals = 2) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
    },

    validateFile(file, allowedExtensions = ['xlsx'], maxSizeMB = 200) {
      if (!file) return false;

      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        this.showWarningMessage(
          t('Only {extensions} files are allowed.').replace(
            '{extensions}',
            `.${allowedExtensions.join(', .')}`
          ),
          t('Warning')
        );
        return false;
      }

      const maxSize = maxSizeMB * 1024 * 1024;
      if (file.size > maxSize) {
        this.showWarningMessage(t('File Size Exceeded Message'), t('Warning'));
        return false;
      }

      return true;
    },

    formatPeriod(periodStr, termType = null) {
      if (!periodStr) return '';
      const cleanStr = String(periodStr).substring(0, 10);
      const parts = cleanStr.split('-');
      const locale = this?.$i18n?.locale === 'tr' ? 'tr-TR' : 'en-US';

      if (termType?.toLowerCase() === 'month' || parts.length === 2) {
        const [year, month] = parts;
        const date = new Date(year, parseInt(month, 10) - 1, 1);
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
      }

      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}.${month}.${year}`;
      }

      return periodStr;
    }
  }
};

export default helper;
